import { useState, useCallback } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Plus, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { useBusinessId } from '@/hooks/useBusinessId';
import { appointmentsApi, type Appointment } from '@/api/appointments';
import { formatTime } from '@/utils/time';
import { todayStr } from '@/utils/date';
import { StatusBadge } from '../components/status-badge';

const STATUS_ORDER: Record<string, number> = {
  CONFIRMED: 0,
  PENDING: 1,
  COMPLETED: 2,
  CANCELLED: 3,
  NO_SHOW: 4,
  EXPIRED: 5,
};

function toDisplayStatus(stateCode: string): 'in-progress' | 'upcoming' | 'completed' | 'cancelled' | 'expired' {
  switch (stateCode) {
    case 'COMPLETED': return 'completed';
    case 'CANCELLED': return 'cancelled';
    case 'NO_SHOW': return 'cancelled';
    case 'EXPIRED': return 'expired';
    default: return 'upcoming';
  }
}

function getInitials(name: string) {
  return name.split(' ').map(n => n[0]).filter(Boolean).join('').toUpperCase().slice(0, 2);
}

function formatTodayHeader() {
  const now = new Date();
  return now.toLocaleDateString('es', { weekday: 'short', month: 'short', day: 'numeric' });
}

export function Today() {
  const businessId = useBusinessId();
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ['appointments', 'today'] });
    setTimeout(() => setRefreshing(false), 500);
  }, [queryClient]);

  const { data: appointments = [], isLoading } = useQuery({
    queryKey: ['appointments', 'today', businessId],
    queryFn: () => appointmentsApi.search(businessId, {
      appointmentDate: todayStr(),
      period: 'day',
    }),
    enabled: !!businessId,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const sorted = [...appointments].sort((a, b) => {
    const diff = (STATUS_ORDER[a.stateCode] ?? 5) - (STATUS_ORDER[b.stateCode] ?? 5);
    if (diff !== 0) return diff;
    return (a.startTime ?? '').localeCompare(b.startTime ?? '');
  });

  const upcoming = sorted.filter(a => a.stateCode !== 'COMPLETED' && a.stateCode !== 'CANCELLED' && a.stateCode !== 'NO_SHOW' && a.stateCode !== 'EXPIRED');
  const completed = sorted.filter(a => a.stateCode === 'COMPLETED');
  const completedCount = completed.length;
  const totalCount = sorted.length;

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: 'var(--cream)' }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--muted-fg)' }} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--cream)' }}>
      <div className="flex-1 overflow-auto" style={{ paddingTop: '16px', paddingLeft: '14px', paddingRight: '14px', paddingBottom: '96px' }}>

        {/* Header */}
        <div className="flex items-center justify-between mb-4" style={{ paddingTop: '4px' }}>
          <div>
            <div className="flex items-baseline gap-2">
              <span style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--dark-teal)' }}>
                {formatTodayHeader()}
              </span>
              <span style={{ fontSize: '13px', color: 'var(--muted-fg)' }}>
                {completedCount}/{totalCount} listas
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center justify-center transition-colors hover:opacity-80"
              style={{ padding: '6px', borderRadius: '8px', color: 'var(--muted-fg)', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            </button>
            <Link
              to="/appointments"
              className="flex items-center gap-1 transition-colors hover:opacity-80"
              style={{ fontSize: '13px', color: 'var(--muted-fg)', padding: '6px 8px', borderRadius: '8px' }}
            >
              <span className="hidden md:inline">Ver todas</span>
              <ChevronRight size={14} />
            </Link>
            <Link
              to="/appointment/new"
              className="flex items-center gap-1.5 transition-colors hover:opacity-90"
              style={{ backgroundColor: 'var(--terracotta)', color: 'var(--off-white)', padding: '6px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 500 }}
            >
              <Plus size={14} strokeWidth={2.5} />
              Nueva
            </Link>
          </div>
        </div>

        {/* Upcoming */}
        {upcoming.length === 0 ? (
          <div
            className="rounded-xl border flex items-center justify-center"
            style={{ backgroundColor: 'var(--off-white)', borderColor: 'var(--border-color)', padding: 'var(--space-4)', minHeight: '200px' }}
          >
            <div className="text-center">
              <div style={{ fontSize: '15px', color: 'var(--muted-fg)', marginBottom: '12px' }}>
                ¡Todo listo por hoy!
              </div>
              <Link
                to="/appointment/new"
                className="inline-flex items-center gap-1.5 transition-colors hover:opacity-90"
                style={{ backgroundColor: 'var(--terracotta)', color: 'var(--off-white)', padding: '8px 20px', borderRadius: '999px', fontSize: '14px', fontWeight: 500 }}
              >
                <Plus size={16} />
                Agendar cita
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {upcoming.map((apt) => (
              <AppointmentCard key={apt.id} appointment={apt} />
            ))}
          </div>
        )}

        {/* Completed */}
        {completedCount > 0 && (
          <div className="mt-4">
            <div className="section-label mb-2 flex items-center gap-2" style={{ fontSize: '11px' }}>
              <span>COMPLETADAS</span>
              <span style={{ backgroundColor: 'var(--dark-teal)', color: 'var(--off-white)', fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: '999px' }}>
                {completedCount}
              </span>
            </div>
            <div className="space-y-2">
              {completed.map((apt) => (
                <Link key={apt.id} to={`/appointment-view/${apt.id}`}>
                  <div
                    className="flex items-center gap-3 rounded-lg transition-colors hover:bg-[#F5F3EF]"
                    style={{ padding: '8px 12px', opacity: 0.7 }}
                  >
                    <div
                      className="rounded-full flex items-center justify-center"
                      style={{ width: '28px', height: '28px', backgroundColor: 'var(--muted-bg)', color: 'var(--muted-fg)', fontSize: '10px', fontWeight: 600 }}
                    >
                      {getInitials(apt.customerName)}
                    </div>
                    <span className="truncate flex-shrink-0 max-w-[45%]" style={{ fontSize: '13px', color: 'var(--muted-fg)' }}>{apt.customerName}</span>
                    <span className="truncate min-w-0" style={{ fontSize: '12px', color: 'var(--muted-fg)', opacity: 0.6 }}>{apt.serviceName}</span>
                    <span className="ml-auto flex-shrink-0" style={{ fontSize: '12px', color: 'var(--muted-fg)', opacity: 0.6 }}>{formatTime(apt.startTime)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AppointmentCard({ appointment: apt }: { appointment: Appointment }) {
  return (
    <Link to={`/appointment-view/${apt.id}`} className="block">
      <div
        className="rounded-2xl transition-all hover:shadow-lg"
        style={{
          backgroundColor: 'var(--off-white)',
          border: apt.stateCode === 'CONFIRMED' ? '2px solid var(--terracotta)' : '1px solid var(--border-color)',
          boxShadow: apt.stateCode === 'CONFIRMED'
            ? '0 4px 20px rgba(224, 123, 84, 0.18)'
            : '0 2px 12px rgba(36, 62, 61, 0.08), 0 1px 3px rgba(36, 62, 61, 0.04)',
          padding: '18px 20px',
        }}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0" style={{ paddingTop: '2px' }}>
            <div
              className="rounded-full flex items-center justify-center"
              style={{ width: '44px', height: '44px', backgroundColor: 'var(--peach)', color: 'var(--dark-teal)', fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: 600 }}
            >
              {getInitials(apt.customerName)}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <span className="truncate min-w-0" style={{ fontFamily: 'var(--font-heading)', fontSize: '15px', color: 'var(--dark-teal)' }}>
                {apt.customerName}
              </span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span style={{ fontFamily: 'var(--font-heading)', fontSize: '15px', color: 'var(--dark-teal)' }}>
                  {formatTime(apt.startTime)}
                </span>
                <StatusBadge status={toDisplayStatus(apt.stateCode)} size="sm" />
              </div>
            </div>
            <div className="flex items-center gap-2 min-w-0" style={{ fontSize: '13px', color: 'var(--muted-fg)' }}>
              <span className="truncate min-w-0">{apt.serviceName}</span>
              {apt.duration && (
                <>
                  <span style={{ opacity: 0.4 }}>·</span>
                  <span className="flex items-center gap-0.5">
                    <Clock size={11} />
                    {apt.duration}m
                  </span>
                </>
              )}
              {apt.userName && (
                <>
                  <span style={{ opacity: 0.4 }}>·</span>
                  <span>{apt.userName}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
